import React, { useState } from 'react';
import {
    Button,
    Space,
    Typography,
    Tag,
    Card,
    message,
    Tooltip,
    Input,
    DatePicker,
    Select
} from 'antd';
import type { Dayjs } from 'dayjs';
import {
    DownloadOutlined,
    EyeOutlined,
    SearchOutlined,
    FileTextOutlined,
    LoadingOutlined
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { IInvoice } from '../../services/nestjs-base-api/types';
import type { ColumnsType } from 'antd/es/table';
import { ResponsiveTable } from '../../admin/components/ResponsiveTable';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface IInvoicesProps {
    invoices: IInvoice[];
    hasMore?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
}

const Invoices: React.FunctionComponent<IInvoicesProps> = ({
    invoices,
    hasMore = false,
    onLoadMore,
    isLoadingMore = false,
}) => {
    const { user } = useAuth();
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

    const handleDownloadInvoice = async (invoice: IInvoice) => {
        if (invoice.invoicePdf) {
            // Direct download from Stripe URL
            window.open(invoice.invoicePdf, '_blank');
            message.success('Invoice download started');
        } else {
            message.error('Invoice PDF not available');
        }
    };

    const handleViewInvoice = (invoice: IInvoice) => {
        if (invoice.hostedInvoiceUrl) {
            window.open(invoice.hostedInvoiceUrl, '_blank');
        } else {
            message.error('Invoice preview not available');
        }
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'paid':
                return 'green';
            case 'pending':
                return 'blue';
            case 'overdue':
                return 'red';
            case 'draft':
                return 'default';
            default:
                return 'orange';
        }
    };

    // Filter invoices based on search criteria
    const filteredInvoices = invoices.filter(invoice => {
        const matchesSearch = searchText === '' || 
            invoice.invoiceNumber.toLowerCase().includes(searchText.toLowerCase()) ||
            invoice.description?.toLowerCase().includes(searchText.toLowerCase());
        
        const matchesStatus = statusFilter === undefined || invoice.status === statusFilter;
        
        const matchesDate = !dateRange || (
            new Date(invoice.invoiceDate) >= dateRange[0]?.toDate()! &&
            new Date(invoice.invoiceDate) <= dateRange[1]?.toDate()!
        );

        return matchesSearch && matchesStatus && matchesDate;
    });

    const columns: ColumnsType<IInvoice> = [
        {
            title: 'Invoice Number',
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
            render: (text: string) => (
                <Space>
                    <FileTextOutlined />
                    <Text strong>#{text}</Text>
                </Space>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'invoiceDate',
            key: 'invoiceDate',
            render: (date: string) => new Date(date).toLocaleDateString(),
            sorter: (a, b) => new Date(a.invoiceDate).getTime() - new Date(b.invoiceDate).getTime(),
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: number, record: IInvoice) => (
                <Text strong>${amount.toFixed(2)} {record.currency.toUpperCase()}</Text>
            ),
            sorter: (a, b) => a.amount - b.amount,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={getStatusColor(status)}>
                    {status}
                </Tag>
            ),
            filters: [
                { text: 'Paid', value: 'Paid' },
                { text: 'Pending', value: 'Pending' },
                { text: 'Overdue', value: 'Overdue' },
                { text: 'Draft', value: 'Draft' },
            ],
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (description: string) => (
                <Tooltip title={description}>
                    <Text type="secondary">{description || 'No description'}</Text>
                </Tooltip>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record: IInvoice) => (
                <Space size="small">
                    <Tooltip title="View Invoice">
                        <Button
                            type="text"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewInvoice(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Download PDF">
                        <Button
                            type="text"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadInvoice(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const uniqueStatuses = Array.from(new Set(invoices.map(inv => inv.status)));

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0 }}>Invoices</Title>
                {invoices.length > 0 && (
                    <Text type="secondary">{filteredInvoices.length} of {invoices.length} invoices</Text>
                )}
            </div>

            {invoices.length === 0 ? (
                <Card>
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <FileTextOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                        <Title level={4} type="secondary">No Invoices Yet</Title>
                        <Text type="secondary">
                            Your invoices will appear here once you have active subscriptions.
                        </Text>
                    </div>
                </Card>
            ) : (
                <>
                    {/* Filters - only shown when there are invoices */}
                    <Card style={{ marginBottom: 16 }}>
                        <Space wrap size="middle">
                            <Input
                                placeholder="Search by invoice number or description"
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ width: 250 }}
                            />
                            <Select
                                placeholder="Filter by status"
                                value={statusFilter}
                                onChange={setStatusFilter}
                                allowClear
                                style={{ width: 150 }}
                            >
                                {uniqueStatuses.map(status => (
                                    <Select.Option key={status} value={status}>
                                        <Tag color={getStatusColor(status)} style={{ margin: 0 }}>
                                            {status}
                                        </Tag>
                                    </Select.Option>
                                ))}
                            </Select>
                            <RangePicker
                                placeholder={['Start date', 'End date']}
                                value={dateRange}
                                onChange={(dates) => setDateRange(dates)}
                            />
                        </Space>
                    </Card>

                    <ResponsiveTable
                        columns={columns}
                        dataSource={filteredInvoices}
                        rowKey="id"
                        pagination={{
                            total: filteredInvoices.length,
                            pageSize: 10,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} invoices`,
                        }}
                        scroll={{ x: 800 }}
                    />

                    {/* Load More button */}
                    {hasMore && onLoadMore && (
                        <div style={{ textAlign: 'center', marginTop: 24 }}>
                            <Button
                                onClick={onLoadMore}
                                loading={isLoadingMore}
                                icon={isLoadingMore ? <LoadingOutlined /> : undefined}
                            >
                                {isLoadingMore ? 'Loading...' : 'Load More Invoices'}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Invoices;